import { z } from 'zod';

export const AdminTeamDto = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  description: z.string().nullable(),
  color: z.string(),
  memberCount: z.number().int(),
  approverCount: z.number().int(),
  leadUserId: z.string().uuid().nullable(),
  leadDisplayName: z.string().nullable(),
  leadUtEid: z.string().nullable(),
});
export type AdminTeamDto = z.infer<typeof AdminTeamDto>;

export const AdminTeamsResponse = z.array(AdminTeamDto);
export type AdminTeamsResponse = z.infer<typeof AdminTeamsResponse>;

export const AdminTeamMemberOtherTeam = z.object({
  teamId: z.string().uuid(),
  teamName: z.string(),
  color: z.string(),
});

export const AdminTeamMemberDto = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  // Avatar initials, computed by the API from the AD name parts
  // (api/src/repositories/person.ts). The web renders this verbatim and
  // must never derive initials itself.
  initials: z.string(),
  utEid: z.string().nullable(),
  email: z.string(),
  role: z.enum(['contributor', 'approver']),
  otherTeams: z.array(AdminTeamMemberOtherTeam),
});
export type AdminTeamMemberDto = z.infer<typeof AdminTeamMemberDto>;

export const AdminTeamMembersResponse = z.array(AdminTeamMemberDto);
export type AdminTeamMembersResponse = z.infer<typeof AdminTeamMembersResponse>;

export const CreateTeamRequest = z.object({
  teamName: z.string().min(1).max(255),
  description: z.string().max(500).optional(),
  color: z.string().max(20).optional(),
  leadUserId: z.string().uuid(),
});
export type CreateTeamRequest = z.infer<typeof CreateTeamRequest>;

export const UpdateTeamRequest = z.object({
  teamName: z.string().min(1).max(255).optional(),
  description: z.string().max(500).nullable().optional(),
  color: z.string().max(20).optional(),
  leadUserId: z.string().uuid().nullable().optional(),
  /**
   * When the lead changes, whether to drop the OUTGOING lead's approver
   * membership down to contributor. Not a property of the team — a directive for
   * this one request.
   *
   * Both leads are added as approvers when they take the role and nothing has
   * ever removed the previous one, so a replaced lead kept team approval scope
   * permanently — and, once analytics is team-scoped, visibility of that team's
   * hours with it. Neither silent default is right: a replaced lead may
   * legitimately stay an approver. So the caller must decide, and the decision is
   * recorded either way.
   */
  revokeOutgoingLeadScope: z.boolean().optional(),
});
export type UpdateTeamRequest = z.infer<typeof UpdateTeamRequest>;

export const AddTeamMemberRequest = z.object({
  userId: z.string().uuid(),
  role: z.enum(['contributor', 'approver']).default('contributor'),
});
export type AddTeamMemberRequest = z.infer<typeof AddTeamMemberRequest>;

export const UpdateTeamMemberRoleRequest = z.object({
  role: z.enum(['contributor', 'approver']),
});
export type UpdateTeamMemberRoleRequest = z.infer<typeof UpdateTeamMemberRoleRequest>;
